import { Routes } from '@angular/router';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';
import { raiseLeaveRequestGuard } from './guards/raise-leave-request.guard';
import { PermissionGuard } from './guards/permission.guard';
import { authGuard } from './guards/auth.guard';
import { impersonationGuard } from './guards/impersonation.guard';

// Route components are loaded lazily (loadComponent) rather than statically
// imported. Static imports pulled every screen into the initial `main` bundle,
// which pushed it past API Gateway/Lambda's 6 MB response limit and returned
// 502 for main.js. Keep new routes lazy.

// `data.breadcrumb` is where a page's place in the trail is declared, and the
// only place — see shared/breadcrumb/breadcrumb.service.ts. Routes without it
// show no breadcrumb, which is deliberate for every page the navbar links to
// directly. `parent` names a navbar tab's route; the crumb takes that tab's
// wording. `label` is the last crumb, or the placeholder the page replaces
// with the record's name once it loads.

export const routes: Routes = [
  // Impersonation ("View As") entry — formerly '/auth'.
  {
    path: 'impersonate',
    loadComponent: () => import('./impersonate/impersonate').then(m => m.ImpersonateComponent),
    // impersonationGuard (not PermissionGuard's requiredGroups) because
    // authority follows the user's assigned roles, not the role they are
    // currently acting as — see the guard for why.
    canActivate: [authGuard, impersonationGuard],
    title: 'Impersonate (View As) - CAPS'
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./unauthorized/unauthorized').then(m => m.UnauthorizedComponent),
    title: 'Unauthorized - CAPS'
  },

  // Protected routes (all require authentication and permissions)
  {
    path: 'pac-feed',
    loadComponent: () => import('./dashboard/pac-feed-list/pac-feed-list.component').then(m => m.PacFeedListComponent),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic_appoint_admin'] },
    title: 'PAC Feed Worklist - CAPS'
  },
  {
    path: 'admin-leave-requests',
    loadComponent: () => import('./admin-leave-requests/admin-leave-requests').then(m => m.AdminLeaveRequestsComponent),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic_appoint_admin', 'academic_appoint_user', 'academic_appoint_user_cuimc', 'department_center_level', 'school_institute_libraries_level', 'vice_provost_faculty_affairs'] },
    title: 'Leave Requests — CAPS'
  },
  {
    path: 'user-management',
    loadComponent: () => import('./user-management/user-management').then(m => m.UserManagementComponent),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic_appoint_admin'] },
    title: 'User Management - CAPS'
  },
  {
    path: 'user-management/search/:uni',
    loadComponent: () => import('./user-management/user-management').then(m => m.UserManagementComponent),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic_appoint_admin'] },
    title: 'User Management - CAPS'
  },
  {
    path: 'user-management/new',
    loadComponent: () => import('./user-management/user-profile').then(m => m.UserProfileComponent),
    canActivate: [authGuard, PermissionGuard],
    data: {
      requiredGroups: ['academic_appoint_admin'],
      breadcrumb: { label: 'Add New User', parent: '/user-management' },
    },
    title: 'Add New User - CAPS'
  },
  {
    path: 'user-management/bulk-assign',
    loadComponent: () => import('./user-management/bulk-assign/bulk-assign').then(m => m.BulkAssignComponent),
    canActivate: [authGuard, PermissionGuard],
    data: {
      requiredGroups: ['academic_appoint_admin'],
      breadcrumb: { label: 'Bulk Assign Roles', parent: '/user-management' },
    },
    title: 'Bulk Assign Roles - CAPS'
  },
  {
    path: 'user-management/:id',
    loadComponent: () => import('./user-management/user-profile').then(m => m.UserProfileComponent),
    canActivate: [authGuard, PermissionGuard],
    data: {
      requiredGroups: ['academic_appoint_admin'],
      breadcrumb: { label: 'User Profile', parent: '/user-management' },
    },
    title: 'User Profile - CAPS'
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./dashboard/academic-appointment-admin/academic-appointment-admin').then(m => m.AcademicAppointmentAdmin),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic_appoint_admin', 'academic_appoint_user', 'academic_appoint_user_cuimc', 'department_center_level', 'school_institute_libraries_level', 'vice_provost_faculty_affairs'] },
    title: 'Dashboard - CAPS'
  },
  {
    path: 'academic-officer/:id',
    loadComponent: () => import('./dashboard/academic-officer/academic-officer-dashboard').then(m => m.AcademicOfficerDashboardComponent),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic-officer'] },
    title: 'Academic Officer Dashboard - CAPS'
  },
  {
    path: 'academic-officer/:id/profile',
    loadComponent: () => import('./profile/profile').then(m => m.Profile),
    canActivate: [authGuard, PermissionGuard],
    data: {
      requiredGroups: ['academic_appoint_admin', 'academic_appoint_user', 'academic_appoint_user_cuimc', 'department_center_level', 'school_institute_libraries_level', 'academic-officer'],
      // No parent: an officer profile hangs directly off home. For an admin
      // that home *is* the officer search they came from.
      breadcrumb: { label: 'Officer Profile' },
    },
    title: 'Academic Officer Profile - CAPS'
  },
  {
    path: 'pac-review/:id',
    loadComponent: () => import('./pac-review/pac-review').then(m => m.PacReviewComponent),
    canActivate: [authGuard, PermissionGuard],
    data: {
      requiredGroups: ['academic_appoint_admin'],
      breadcrumb: { label: 'PAC Review', parent: '/pac-feed' },
    },
    title: 'PAC Review - CAPS'
  },
  {
    path: 'leave-request/new',
    loadComponent: () => import('./leave-request/leave-request-form').then(m => m.LeaveRequestFormComponent),
    canActivate: [authGuard, PermissionGuard, raiseLeaveRequestGuard],
    canDeactivate: [unsavedChangesGuard],
    // An officer raising their own leave has no "Leave Requests" tab, so this
    // parent is dropped for them and the trail is just home > New Leave Request.
    data: { breadcrumb: { label: 'New Leave Request', parent: '/admin-leave-requests' } },
    title: 'New Leave Request - CAPS'
  },
  {
    path: 'leave-request/:id',
    loadComponent: () => import('./leave-request/leave-request-form').then(m => m.LeaveRequestFormComponent),
    canActivate: [authGuard, PermissionGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { breadcrumb: { label: 'Leave Request', parent: '/admin-leave-requests' } },
    title: 'Edit Leave Request - CAPS'
  },
  {
    path: 'leave-request',
    loadComponent: () => import('./leave-request/leave-request-form').then(m => m.LeaveRequestFormComponent),
    canActivate: [authGuard, PermissionGuard, raiseLeaveRequestGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { breadcrumb: { label: 'Leave Request', parent: '/admin-leave-requests' } },
    title: 'Leave Request - CAPS'
  },

  {
    path: 'academic-officer-search',
    loadComponent: () => import('./search/advanced-search/advanced-search').then(m => m.AdvancedSearchComponent),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic_appoint_admin', 'academic_appoint_user', 'academic_appoint_user_cuimc', 'department_center_level', 'school_institute_libraries_level'] },
    title: 'Advanced Officer Search - CAPS'
  },
  {
    path: 'report',
    loadComponent: () => import('./report/report').then(m => m.ReportComponent),
    canActivate: [authGuard, PermissionGuard],
    data: { requiredGroups: ['academic_appoint_admin', 'academic_appoint_user', 'academic_appoint_user_cuimc', 'department_center_level', 'school_institute_libraries_level'] },
    title: 'Reports - CAPS'
  },

  // Default route - login page handled in app.ts
  { path: '**', redirectTo: '' },
];
